import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'
import { FiPlus } from 'react-icons/fi'
import { toast } from 'react-toastify'
import * as Actions from '../../store/action/claim.js'
import { getPolicyDetails, getGoodsForPolicy, getAllPolicyUsages } from '../../store/action/policy'
import { getPersonDetails, CheckPersonExistence } from '../../store/action/party'
import NouveauSinistreForm from '../../components/NouveauSinistreForm'
import { handleSubmitClaimNew } from '../../utils/handleSubmitClaimNew'

const useStyles = makeStyles(() => ({
  addButton: {
    fontFamily: 'Poppins',
    fontSize: '10.77px',
    fontWeight: 600,
    lineHeight: '16.16px',
    color: 'rgba(255, 255, 255, 1)',
    background: 'rgb(186, 213, 58)',
    width: '140px',
    height: '30px',
    borderRadius: '5.39px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginLeft: '15px',
  },
  dialogPaper: {
    overflow: 'hidden',
  },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 14,
    color: 'rgba(63, 66, 84, 1)',
    backgroundColor: 'rgba(243, 246, 249, 1)',
    borderBottom: '1px solid rgba(243, 246, 249, 1)',
    padding: '10px 16px',
    position: 'relative',
  },
  dialogActions: {
    backgroundColor: 'rgba(243, 246, 249, 1)',
    padding: '10px 16px',
    gap: 8,
  },
  CancelStyle: {
    height: 32,
    padding: '0 18px',
    color: 'rgb(255, 54, 54)',
    backgroundColor: 'rgb(255, 229, 229)',
    borderRadius: 5,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    border: 0,
    cursor: 'pointer',
  },
  ajouterStyle: {
    height: 32,
    minWidth: 90,
    padding: '0 18px',
    backgroundColor: 'rgba(187, 200, 8, 1)',
    color: 'rgba(255, 255, 255, 1)',
    borderRadius: 5,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    border: 0,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.6, cursor: 'wait' },
  },
  titreLabel: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 13,
    color: 'rgba(63, 66, 84, 1)',
  },
  // Fields fill their grid column and use the same font as the labels
  // (inputs, selects and date pickers otherwise fall back to the browser's Arial)
  selectInputDialog: {
    width: '100%',
    boxSizing: 'border-box',
    height: 34,
    padding: '0 10px',
    border: '1px solid transparent',
    borderRadius: 5,
    background: 'rgba(243, 246, 249, 1)',
    fontFamily: 'Poppins',
    fontSize: 12,
    color: 'rgba(63, 66, 84, 1)',
    outline: 'none',
    '&:focus': {
      borderColor: 'rgba(187, 200, 8, 1)',
      background: 'white',
    },
    '&:disabled': { opacity: 0.6 },
    '&[multiple]': { height: 120, padding: 6 },
  },
  modalLabel: {
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '18px',
    marginBottom: 4,
    color: 'rgba(70, 78, 95, 1)',
  },
  policeInputContainer: {
    position: 'relative',
    width: '100%',
  },
  policeSearchBtn: {
    position: 'absolute',
    right: 0,
    top: 0,
    height: 34,
    width: 34,
    border: 0,
    background: 'rgba(60, 101, 86, 1)',
    color: 'white',
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
    cursor: 'pointer',
  },
}))

// Characteristic keys as stored in MS_POLICY_DB.goodcaracteristiques
// (normalised by ms-policy: lowercase then first letter uppercase)
const VEHICLE_FIELDS = {
  License_plate: 'vehicleRegistrationNumber',
  Brand: 'vehicleBrand',
  Model: 'vehicleModel',
  Firstcirculationdate: 'vehicleFirstCirculationDate',
  Power: 'vehiclePower',
  Seatsnumber: 'vehicleSeatsNumber',
  Enginenumber: 'vehicleEngineNumber',
  Chassisnumber: 'vehicleChassisNumber',
}

const DATE_FIELDS = ['vehicleFirstCirculationDate']

const toInputDate = (value) => {
  const date = new Date(value)
  return isNaN(date.getTime()) ? String(value) : date.toISOString().split('T')[0]
}

// The form is uncontrolled (read through querySelector on submit), so the
// search results are written straight into its fields.
const setField = (name, value) => {
  if (value == null || value === '') return
  const field = document.querySelector(`[name="${name}"]`)
  if (field) field.value = value
}

/**
 * "Ajouter Sinistre" button and creation dialog (MAE). The claim goes through
 * the gateway orchestration used by the ETL: /api/claimcreation/create-complete-claim.
 */
function AddClaimDialog({ coverageType, claimTypeOptions, expertOptions, onCreated }) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPolicySearching, setIsPolicySearching] = useState(false)
  const [foundPolicy, setFoundPolicy] = useState(null)
  const [agencies, setAgencies] = useState([])
  const [policyUsages, setPolicyUsages] = useState([])

  useEffect(() => {
    if (open) setFoundPolicy(null)
  }, [open])

  useEffect(() => {
    if (!open || agencies.length) return
    dispatch(Actions.getAllAgency()).then((data) => setAgencies(Array.isArray(data) ? data : []))
    getAllPolicyUsages().then(setPolicyUsages)
  }, [open])

  const handlePolicySearch = async () => {
    const policyNumber = document.querySelector('input[name="policyNumber"]')?.value?.trim()
    if (!/^[A-Za-z0-9]{4,20}$/.test(policyNumber || '')) {
      toast.warning('Le numéro de police doit contenir de 4 à 20 lettres ou chiffres')
      return
    }

    setIsPolicySearching(true)
    try {
      const policy = await getPolicyDetails(policyNumber)
      if (!policy?.policyNumber) {
        setFoundPolicy(null)
        toast.error('Police introuvable')
        return
      }
      setFoundPolicy(policy)

      const [holder, goods] = await Promise.all([
        policy.policyHolder ? getPersonDetails(policy.policyHolder) : null,
        getGoodsForPolicy(policy.policyNumber),
      ])

      if (holder) {
        setField('policyHolderFirstName', holder.name)
        setField('policyHolderLastName', holder.lastName)
        setField('policyHolderCode', holder.code)
        setField('policyHolderIdentityNumber', holder.identityNumber)
        if (holder.birthDate) setField('policyHolderBirthDate', toInputDate(holder.birthDate))
      }

      if (policy.subscriptionDate) setField('policyEffectDate', toInputDate(policy.subscriptionDate))
      if (policy.expirationDate) setField('policyExpirationDate', toInputDate(policy.expirationDate))
      setField('policyType', policy.policyType)
      setField('policyUsage', policy.policyUsage?._id || policy.policyUsage)
      setField('declarationAgency', policy.agency?._id)
      setField('agencyLabel', policy.agency?.label)

      const characteristics = Array.isArray(goods) ? goods[0]?.goodCaracteristique || [] : []
      characteristics.forEach(({ key, value }) => {
        const field = VEHICLE_FIELDS[key]
        if (!field) return
        setField(field, DATE_FIELDS.includes(field) ? toInputDate(value) : value)
        if (field === 'vehicleRegistrationNumber') setField('registrationNumber', value)
      })

      toast.success('Police trouvée ! Les champs ont été remplis automatiquement')
    } catch (error) {
      console.error('[AddClaimDialog] policy search', error)
      setFoundPolicy(null)
      toast.error('Erreur lors de la recherche de la police')
    } finally {
      setIsPolicySearching(false)
    }
  }

  const handleThirdPartySearch = async () => {
    const identityNumber = document.querySelector('input[name="thirdPartyIdentityNumber"]')?.value?.trim()
    if (!identityNumber) {
      toast.warning("Veuillez entrer un numéro d'identité")
      return
    }

    setIsPolicySearching(true)
    try {
      const party = await CheckPersonExistence({ identityNumber })
      if (!party?.uid) {
        toast.info('Personne non trouvée dans la base. Un nouveau tiers sera créé.')
        return
      }
      const person = await getPersonDetails(party.uid)
      if (!person) return

      setField('thirdPartyFirstName', person.name)
      setField('thirdPartyLastName', person.lastName)
      if (person.birthDate) setField('thirdPartyBirthDate', toInputDate(person.birthDate))

      const address = person.partyAddresses?.[0]?.address
      if (address) {
        setField('thirdPartyAddress', `${address.number || ''} ${address.street || ''}`.trim())
        setField('thirdPartyCity', address.city || address.state?.label)
      }

      toast.success(`Informations trouvées pour ${`${person.name || ''} ${person.lastName || ''}`.trim()}`)
    } catch (error) {
      console.error('[AddClaimDialog] third party search', error)
      toast.error('Erreur lors de la recherche du tiers')
    } finally {
      setIsPolicySearching(false)
    }
  }

  const handleSubmit = () => handleSubmitClaimNew(dispatch, Actions, setIsSubmitting, setOpen, onCreated)

  return (
    <>
      <button className={classes.addButton} onClick={() => setOpen(true)}>
        <FiPlus style={{ height: '18px', width: '18px' }} />
        Ajouter Sinistre
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} classes={{ paper: classes.dialogPaper }} fullWidth maxWidth="md">
        <DialogTitle className={classes.dialogTitle}>
          <span style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 13 }}>Nouveau sinistre</span>
          <span
            style={{ position: 'absolute', right: 16, top: 8, fontSize: 18, cursor: 'pointer', color: 'rgba(63, 66, 84, 1)' }}
            onClick={() => setOpen(false)}
          >
            ✕
          </span>
        </DialogTitle>
        <DialogContent style={{ backgroundColor: 'white', padding: 0 }}>
          <NouveauSinistreForm
            classes={classes}
            isPolicySearching={isPolicySearching}
            foundPolicy={foundPolicy}
            onPolicyNumberChange={() => setFoundPolicy(null)}
            handlePolicySearch={handlePolicySearch}
            handleThirdPartySearch={handleThirdPartySearch}
            agencies={agencies}
            coverageType={coverageType}
            claimTypeOptions={claimTypeOptions}
            expertOptions={expertOptions}
            policyUsages={policyUsages}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <button className={classes.CancelStyle} onClick={() => setOpen(false)}>
            Annuler
          </button>
          <button className={classes.ajouterStyle} onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Création en cours...' : 'Valider'}
          </button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AddClaimDialog
